import React, { useEffect, useState } from "react";
import { X, Copy, ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";

const CURRENCIES = [
  { id: "USDT", label: "USDT" },
  { id: "BTC",  label: "BTC"  },
  { id: "ETH",  label: "ETH"  },
];

export default function PriorityCheckoutModal({ open, onClose }) {
  const { theme } = useTheme();

  const [step,     setStep]     = useState(1);
  const [email,    setEmail]    = useState("");
  const [name,     setName]     = useState("");
  const [currency, setCurrency] = useState("USDT");
  const [payment,  setPayment]  = useState(null);
  const [status,   setStatus]   = useState(null);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [copied,   setCopied]   = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1); setPayment(null); setStatus(null);
      setError(""); setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!payment?.payment_id) return;
    let live = true;
    const poll = async () => {
      try {
        const res = await api.get(`/priority/status/${payment.payment_id}`);
        if (!live) return;
        setStatus(res.data);
        if (res.data.status === "finished") setStep(3);
      } catch (e) {}
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { live = false; clearInterval(t); };
  }, [payment?.payment_id]);

  const onCreate = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Enter your email."); return; }
    setLoading(true);
    try {
      const res = await api.post("/priority/create-payment", {
        email: email.trim(), currency, name: name.trim() || null,
      });
      setPayment(res.data);
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Payment gateway unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onSimulate = async () => {
    if (!payment?.payment_id) return;
    setLoading(true);
    try {
      await api.post("/priority/simulate-confirm", { payment_id: payment.payment_id });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = () => {
    if (!payment?.pay_address) return;
    navigator.clipboard.writeText(payment.pay_address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPortal>
        <DialogOverlay className="bg-black/80 backdrop-blur-md" />
        <DialogContent
          className="max-w-lg w-[92vw] p-0 bg-[#0A0A0A] border text-white"
          style={{ borderColor: theme.color, boxShadow: `0 0 60px ${theme.color}55` }}
        >
          <DialogTitle className="sr-only">Priority Premium Checkout</DialogTitle>
          <DialogDescription className="sr-only">Secure your priority access slot.</DialogDescription>

          <div className="relative">
            <div className="h-1 w-full" style={{ backgroundColor: theme.color }} />
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                    Priority Premium · Step {step} of 3
                  </div>
                  <h3 className="font-display font-black text-2xl">
                    {step === 1 && "Secure your spot"}
                    {step === 2 && `Send $${payment?.price_amount ?? 50} in ${payment?.pay_currency?.toUpperCase()}`}
                    {step === 3 && "You're in"}
                  </h3>
                </div>
                <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex items-center gap-2 mb-8">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="h-[3px] flex-1 rounded-full"
                    style={{ backgroundColor: step >= n ? theme.color : "rgba(255,255,255,0.08)" }} />
                ))}
              </div>

              {/* ── STEP 1 ── */}
              {step === 1 && (
                <form onSubmit={onCreate} className="space-y-4">
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Email</span>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      required placeholder="you@domain.com"
                      className="w-full h-11 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm" />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Name (optional)</span>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="w-full h-11 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm" />
                  </label>
                  <div>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Pay with</span>
                    <div className="grid grid-cols-3 gap-2">
                      {CURRENCIES.map((c) => {
                        const active = c.id === currency;
                        return (
                          <button type="button" key={c.id} onClick={() => setCurrency(c.id)}
                            className="h-11 border text-sm font-display font-bold tracking-wide transition-colors"
                            style={{
                              borderColor: active ? theme.color : "rgba(255,255,255,0.12)",
                              color: active ? theme.color : "#a1a1aa",
                              backgroundColor: active ? `${theme.color}10` : "transparent",
                              boxShadow: active ? `0 0 20px ${theme.color}33` : "none",
                            }}>
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 border border-red-500/25 bg-red-500/5">
                      <p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}

                  <button type="submit" disabled={loading}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 h-12 font-display font-bold tracking-wide disabled:opacity-60"
                    style={{ backgroundColor: theme.color, color: "#000", boxShadow: `0 0 28px ${theme.color}77` }}>
                    {loading ? "CREATING PAYMENT…" : "GENERATE DEPOSIT ADDRESS"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <p className="text-[11px] text-zinc-500 text-center pt-1">
                    One-time $50 payment. 2 months free Premium post-launch. 30-min checkout window.
                  </p>
                </form>
              )}

              {/* ── STEP 2 ── */}
              {step === 2 && payment && (
                <div className="space-y-4">
                  <div className="p-5 border bg-black/40" style={{ borderColor: theme.color }}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Send exactly</div>
                    <div className="font-display font-black text-3xl theme-text">
                      {payment.pay_amount} {payment.pay_currency?.toUpperCase()}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">≈ ${payment.price_amount} USD</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Deposit address</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 p-3 font-mono text-xs bg-black/60 border border-white/10 break-all">
                        {payment.pay_address}
                      </div>
                      <button onClick={copyAddress}
                        className="h-11 px-3 border border-white/10 hover:border-white/30 transition-colors">
                        {copied ? <Check className="h-4 w-4 theme-text" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 border border-white/10 bg-black/30 text-xs">
                    <span className="text-zinc-500">Status</span>
                    <span className="theme-text font-mono uppercase tracking-[0.2em]">
                      {(status?.status || payment.status || "pending").toString()}
                    </span>
                  </div>
                  {payment.simulated && (
                    <button onClick={onSimulate} disabled={loading}
                      className="w-full h-11 border text-sm font-display font-bold tracking-wide"
                      style={{ borderColor: theme.color, color: theme.color }}>
                      {loading ? "SIMULATING…" : "SIMULATE PAYMENT (sandbox)"}
                    </button>
                  )}
                  <p className="text-[11px] text-zinc-500 text-center">
                    Payment auto-confirms on-chain. This screen updates automatically.
                  </p>
                </div>
              )}

              {/* ── STEP 3 ── */}
              {step === 3 && (
                <div className="text-center py-6 space-y-5">
                  <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center pulse-glow"
                    style={{ backgroundColor: theme.color }}>
                    <Check className="h-8 w-8 text-black" strokeWidth={3} />
                  </div>
                  <h4 className="font-display font-black text-xl">Priority access unlocked</h4>
                  <p className="text-sm text-zinc-300 max-w-sm mx-auto leading-relaxed">
                    Your 2 months of free Premium bot usage are secured.
                  </p>
                  <button onClick={onClose}
                    className="mt-2 inline-flex items-center justify-center h-11 px-8 border text-sm font-display font-bold tracking-wide"
                    style={{ borderColor: theme.color, color: theme.color }}>
                    CLOSE
                  </button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
